import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { faqService, FAQ as FAQType } from '../services/faqService';
import { contactService } from '../services/contactService';
import CardSkeleton from '../components/CardSkeleton';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import heroData from '../data/heroData.json';
import { useAuth } from '../contexts/AuthContext';
import { countries } from '../data/countries';

const Contact = () => {
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    message: '',
  });
  const [faqs, setFaqs] = useState<FAQType[]>([]);
  const [faqsLoading, setFaqsLoading] = useState(true);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Prefill form when user is logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      const fullName = user.firstName && user.lastName 
        ? `${user.firstName} ${user.lastName}`.trim()
        : user.firstName || user.lastName || '';
      
      setFormData(prev => ({
        ...prev,
        name: fullName || prev.name,
        email: user.email || prev.email,
      }));
    }
  }, [isAuthenticated, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      await contactService.sendMessage(formData);
      setSubmitSuccess(true);
      // Reset form
      setFormData({
        name: '',
        email: '',
        country: '',
        message: '',
      });
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || err.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        setFaqsLoading(true);
        // Fetch a limited number of FAQs for the contact page (e.g., 6)
        const response = await faqService.getFAQs({ limit: 6, isActive: true });
        
        if (response.success && response.data) {
          // Handle both array and paginated response
          const faqsData = Array.isArray(response.data) 
            ? response.data 
            : (response.data as any).faqs || [];
          
          // Sort by order, then by createdAt
          const sortedFaqs = faqsData.sort((a: FAQType, b: FAQType) => {
            if (a.order !== b.order) {
              return a.order - b.order;
            }
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          });
          
          setFaqs(sortedFaqs);
        }
      } catch (err) {
        console.error('Error fetching FAQs:', err);
        // Silently fail - don't show error to user, just don't display FAQs
      } finally {
        setFaqsLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  return (
    <>
      <SEO
        title="Contact Us - Get in Touch for Yoga Teacher Training Guidance"
        description="Ready to transform your life through authentic yoga practice? We're here to guide you every step of the way on your sacred journey. Contact us for expert consultation."
        keywords="contact yoga teacher training, yoga consultation contact, yoga school inquiry, yoga training support"
        url="https://bookmyyogattc.com/contact"
        canonical="https://bookmyyogattc.com/contact"
      />
      <div className="pt-20">
      <HeroSection
        title={heroData.contact.title}
        description={heroData.contact.description}
        patternId="contact"
      />

      {/* Contact Form & Info */}
      <section className="py-20 bg-fog">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl shadow-xl p-8"
            >
              <h2 className="text-5 font-serif font-semibold text-midnight mb-6 tracking-tight">
                Contact Us
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-midnight/70 font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-midnight/10 rounded-lg focus:ring-2 focus:ring-orange focus:border-orange transition-colors text-midnight bg-white"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-midnight/70 font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-midnight/10 rounded-lg focus:ring-2 focus:ring-orange focus:border-orange transition-colors text-midnight bg-white"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="country" className="block text-midnight/70 font-medium mb-2">
                    Country *
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-midnight/10 rounded-lg focus:ring-2 focus:ring-orange focus:border-orange transition-colors text-midnight appearance-none bg-white"
                    style={{ maxHeight: '200px' }}
                  >
                    <option value="">Select your country</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-midnight/70 font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-midnight/10 rounded-lg focus:ring-2 focus:ring-orange focus:border-orange transition-colors resize-none text-midnight bg-white"
                    placeholder="How can we help you? Share your questions or inquiries..."
                  />
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {submitError}
                  </div>
                )}

                {submitSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    Message sent successfully! We'll get back to you within 24 hours.
                  </div>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-orange text-white py-4 rounded-lg font-semibold text-4 hover:bg-orange-dark transition-colors duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </motion.button>
              </form>


            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-5 font-serif font-semibold text-midnight mb-6 tracking-tight">
                  Get in Touch
                </h2>
                <p className="text-midnight/70 text-4 leading-relaxed mb-8">
                  Have questions about our programs? We're here to help you find the perfect path 
                  for your yoga journey. Reach out to us through any of the following channels.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-6 bg-fog rounded-xl">
                  <div className="text-5">✉️</div>
                  <div>
                    <h3 className="text-5 font-serif font-semibold text-midnight mb-2">Email</h3>
                    <p className="text-midnight/70">
                      info@bookmyyogattc.com<br />
                      admissions@bookmyyogattc.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-fog rounded-xl">
                  <div className="text-5">🌐</div>
                  <div>
                    <h3 className="text-5 font-serif font-semibold text-midnight mb-2">Follow Us</h3>
                    <div className="flex space-x-4">
                      <a href="#" className="text-orange hover:text-orange-dark transition-colors">
                        Facebook
                      </a>
                      <a href="#" className="text-orange hover:text-orange-dark transition-colors">
                        Instagram
                      </a>
                      <a href="#" className="text-primary hover:text-primary-dark transition-colors">
                        YouTube
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Info */}
              <div className="bg-primary text-white p-8 rounded-2xl">
                <h3 className="text-5 font-serif font-light mb-4 tracking-wide">
                  Application Process
                </h3>
                <div className="space-y-3 text-2 opacity-95">
                  <div className="flex items-center space-x-3">
                    <span className="text-accent text-4 w-6 flex-shrink-0">1.</span>
                    <span>Submit your application form</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-accent text-4 w-6 flex-shrink-0">2.</span>
                    <span>Receive confirmation within 24 hours</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-accent text-4 w-6 flex-shrink-0">3.</span>
                    <span>Complete registration with deposit</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-accent text-4 w-6 flex-shrink-0">4.</span>
                    <span>Receive pre-course materials</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-accent text-4 w-6 flex-shrink-0">5.</span>
                    <span>Begin your transformation journey</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-fog">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-section font-serif font-light mb-6 tracking-wide text-midnight">
              Frequently Asked Questions
            </h2>
            <p className="text-5 text-midnight/70 max-w-3xl mx-auto font-light">
              Find answers to common questions about our yoga teacher training programs 
              and life at our ashram in Rishikesh.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqsLoading ? (
              <CardSkeleton count={6} type="faq" />
            ) : faqs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-midnight/70">No FAQs available at the moment.</p>
              </div>
            ) : (
              faqs.map((faq, index) => (
                <motion.div
                  key={faq._id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-sm border border-midnight/5 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full py-6 px-6 bg-forest text-white flex justify-between items-center text-left hover:brightness-110 transition-all duration-300"
                  >
                    <h3 className="text-4 font-medium pr-4">
                      {faq.question}
                    </h3>
                    <div className={`text-5 transition-transform duration-300 flex-shrink-0 ${
                      openFAQ === index ? 'rotate-45' : ''
                    }`}>
                      +
                    </div>
                  </button>
                  
                  <motion.div
                    initial={false}
                    animate={{
                      height: openFAQ === index ? 'auto' : 0,
                      opacity: openFAQ === index ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6">
                      <p className="text-midnight/70 font-light leading-relaxed whitespace-pre-line">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Contact; 